import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { TemplateType } from '~/types/template';

import {
  buildPAOrderBy,
  buildPAQueryVariables,
  convertRowsToPageAnalytics,
  extractPagePaths,
  extractTotalPagesFromResponse,
  normalizePAListingSortSelected,
} from '../helpers/pageAnalyticV2';
import { useMetricsSelectorStore } from '../stores/metrics-selector';
import { EPageDimension, EPageMetric } from '~/core/gemxql/types';
import { useCustomPageAnalyticQueryV2 } from '../api';
import { EMetricKey } from '~/types/metric';
import { useMemo } from 'react';

export interface IFetchPageAnalyticsV2Props {
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  mode: EAnalyticMode;
  sortSelected?: string[];
  searchValue?: string;
  filterPageTypes?: TemplateType[];
  limit: number;
  offset: number;
}
export const useFetchPageAnalyticsV2 = ({
  isQueryPreviousData,
  mode,
  currentDates,
  previousDates,
  sortSelected,
  searchValue,
  filterPageTypes,
  limit,
  offset,
}: IFetchPageAnalyticsV2Props) => {
  const selectedMetrics = useMetricsSelectorStore((state) => state.selectedMetrics);

  const metrics = useMemo(
    () => Array.from(new Set([EMetricKey.SESSION, ...selectedMetrics, EPageMetric.PAGE_ITEMS])),
    [selectedMetrics],
  );
  const effectiveSortSelected = useMemo(
    () => normalizePAListingSortSelected(sortSelected, selectedMetrics),
    [sortSelected, selectedMetrics],
  );
  const orderBy = useMemo(() => buildPAOrderBy(effectiveSortSelected), [effectiveSortSelected]);

  const baseVariables = useMemo(
    () => ({ mode, searchValue, filterPageTypes }),
    [mode, searchValue, filterPageTypes],
  );
  const pageListingVariables = useMemo(
    () => ({
      ...baseVariables,
      metrics,
      dimensions: [EPageDimension.PAGE_PATH],
      orderBy,
      limit,
    }),
    [baseVariables, metrics, orderBy, limit],
  );

  const currentVariables = useMemo(
    () => buildPAQueryVariables({ ...pageListingVariables, dateRange: currentDates, offset }),
    [pageListingVariables, currentDates, offset],
  );
  const totalPagesVariables = useMemo(
    () =>
      buildPAQueryVariables({
        ...baseVariables,
        metrics: [EPageMetric.PAGE_PATHS],
        dimensions: [],
        dateRange: currentDates,
      }),
    [baseVariables, currentDates],
  );

  const {
    data: currentResponse,
    isFetching,
    isError,
    refetch,
  } = useCustomPageAnalyticQueryV2(currentVariables, {
    enabled: !!currentVariables.input,
  });

  const currentResult = useMemo(
    () => convertRowsToPageAnalytics(currentResponse),
    [currentResponse],
  );

  // The previous-period query only compares the page paths surfaced by the
  // current-period query, so it waits for the current query to resolve, then
  // filters by exactly those paths — no offset, since the current page is already scoped.
  const currentPagePaths = useMemo(
    () => extractPagePaths(currentResult.items),
    [currentResult.items],
  );
  const previousVariables = useMemo(
    () =>
      buildPAQueryVariables({
        ...pageListingVariables,
        dateRange: previousDates,
        filterPagePaths: currentPagePaths,
      }),
    [pageListingVariables, previousDates, currentPagePaths],
  );

  const isEnabledPreviousQuery =
    !!previousVariables.input &&
    !!isQueryPreviousData &&
    !isFetching &&
    currentPagePaths.length > 0;
  const { data: previousResponse, isFetching: isFetchingPrevious } = useCustomPageAnalyticQueryV2(
    previousVariables,
    {
      enabled: isEnabledPreviousQuery,
    },
  );

  const { data: totalPagesResponse, isFetching: isFetchingTotalPages } =
    useCustomPageAnalyticQueryV2(totalPagesVariables, {
      enabled: !!totalPagesVariables.input,
    });

  const previousResult = useMemo(
    () => convertRowsToPageAnalytics(previousResponse),
    [previousResponse],
  );
  const totalRecords = useMemo(
    () => extractTotalPagesFromResponse(totalPagesResponse),
    [totalPagesResponse],
  );

  return {
    data: currentResult.items,
    dataPrevious: previousResult.items,
    columnTypes: currentResult.columnTypes,
    totalRecords,
    isFetching: isFetching || isFetchingTotalPages,
    isFetchingPrevious: currentPagePaths.length > 0 ? isFetchingPrevious : false,
    isError,
    refetch,
  };
};
